/**
 * Instant Add-to-Cart Handler — v9 (fast multi-item path)
 *
 * Strategy:
 *  - Try to read the variant price from the button (data-price added in
 *    card.liquid). If found, fetch /cart.js in parallel and decide bonus
 *    BEFORE the POST → single multi-item POST with sections.
 *  - Fallback (no data-price, e.g. AI Product app buttons): POST main,
 *    then check /cart.js, then maybe POST bonus.
 *  - Drawer opens ONCE at the end via cartDrawer.renderContents — already
 *    populated with main + bonus + bonus styling.
 */
(function () {
  "use strict";

  var BONUS_VARIANT_ID = 47354961887490;
  var BONUS_THRESHOLD_KOP = 50000;
  var BONUS_PROP_KEY = "_bonus_gift";

  let initialized = false;

  // Serialize the read-cart → decide-bonus → POST critical section so that
  // concurrent clicks (multiple buttons, multiple tabs sharing same cookies)
  // can't each observe hasBonus=false and each push a duplicate bonus.
  let __addQueue = Promise.resolve();

  function initInstantAdd() {
    if (initialized) return;
    initialized = true;
    document.addEventListener("click", handleInstantAddClick, true);
    document.addEventListener("click", handleRemoveClick, true);
    patchDrawerSelectiveUpdate();
  }

  // Surgical drawer update — replaces ONLY the changed parts, leaves
  // bonus-progress-bar element in place so its CSS transition animates
  // smoothly from old → new width.
  function applySurgicalDrawerUpdate(parsedSections, opts) {
    opts = opts || {};
    const changedLine = opts.changedLine; // 1-based; null = full items list replace

    if (parsedSections && parsedSections["cart-drawer"]) {
      const doc = new DOMParser().parseFromString(
        parsedSections["cart-drawer"],
        "text/html",
      );

      if (changedLine != null) {
        // Ultra-surgical: only replace the price wrapper (line total).
        // Quantity input value is already updated synchronously by the
        // caller, image/title/counter buttons don't need to change.
        const newLi = doc.getElementById(`CartDrawer-Item-${changedLine}`);
        const currentLi = document.getElementById(
          `CartDrawer-Item-${changedLine}`,
        );
        if (newLi && currentLi) {
          const newPrice = newLi.querySelector(".wt-cart__item__price-wrapper");
          const curPrice = currentLi.querySelector(
            ".wt-cart__item__price-wrapper",
          );
          if (newPrice && curPrice) {
            curPrice.innerHTML = newPrice.innerHTML;
          }
        }
      } else {
        // Full items list replacement (used for atomic remove / add)
        const newItems = doc.getElementById("CartDrawer-CartItems");
        const currentItems = document.getElementById("CartDrawer-CartItems");
        if (newItems && currentItems) {
          currentItems.innerHTML = newItems.innerHTML;
        }
      }

      // Header title with item count (e.g. "ВАШ КОШИК (3)")
      const newTotalQty = doc.getElementById("CartDrawer-TotalQty");
      const curTotalQty = document.getElementById("CartDrawer-TotalQty");
      if (newTotalQty && curTotalQty) {
        curTotalQty.innerHTML = newTotalQty.innerHTML;
      }

      // Footer (may be absent in empty cart)
      const newFooter = doc.querySelector(".wt-cart__drawer__footer");
      const currentFooter = document.querySelector(".wt-cart__drawer__footer");
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
        currentFooter.style.display = "";
      } else if (!newFooter && currentFooter) {
        currentFooter.style.display = "none";
      }

      // Toggle empty class on the cart-drawer wrapper
      const cartDrawerWrapper = document.querySelector("cart-drawer");
      const newCartDrawer = doc.querySelector("cart-drawer");
      if (cartDrawerWrapper && newCartDrawer) {
        cartDrawerWrapper.classList.toggle(
          "wt-cart--empty",
          newCartDrawer.classList.contains("wt-cart--empty"),
        );
      }
    }

    if (parsedSections && parsedSections["cart-icon-bubble"]) {
      const sec = new DOMParser()
        .parseFromString(parsedSections["cart-icon-bubble"], "text/html")
        .querySelector(".shopify-section");
      const currentBubble = document.getElementById("cart-icon-bubble");
      if (sec && currentBubble) currentBubble.innerHTML = sec.innerHTML;
    }
  }

  // Override CartDrawerItems.updateQuantity so +/- updates only the changed
  // line item, not the entire items list. The bonus-progress-bar element
  // stays in DOM and smoothly transitions to the new width via CSS.
  function patchDrawerSelectiveUpdate() {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements
      .whenDefined("cart-drawer-items")
      .then(() => {
        const klass = customElements.get("cart-drawer-items");
        if (!klass || !klass.prototype || klass.prototype.__surgicalPatched)
          return;
        klass.prototype.__surgicalPatched = true;

        // Patch onChange so qty=0 (from clicking - at qty=1) flows through
        // to updateQuantity instead of being clamped to 1.
        klass.prototype.onChange = function (event) {
          let qty = parseInt(event.target.value, 10);
          if (isNaN(qty) || qty < 0) qty = 1; // allow 0, clamp only negatives
          if (qty > 100) qty = 100;
          event.target.value = qty;
          this.updateQuantity(
            event.target.dataset.index,
            qty,
            document.activeElement &&
              document.activeElement.getAttribute &&
              document.activeElement.getAttribute("name"),
          );
        };

        klass.prototype.updateQuantity = function (line, quantity, name) {
          quantity = parseInt(quantity, 10);
          if (isNaN(quantity) || quantity < 0) quantity = 1;
          if (quantity > 100) quantity = 100;

          // Special case: qty going to 0 while bonus is in cart and this is
          // the last non-bonus item → also drop the bonus atomically
          if (quantity === 0) {
            const cartItemsList = document.querySelectorAll(
              "cart-drawer-items .wt-cart__list > li",
            );
            const bonusMark = String(BONUS_VARIANT_ID);
            let nonBonusCount = 0;
            let bonusLi = null;
            cartItemsList.forEach((li) => {
              if (li.innerHTML.indexOf(bonusMark) !== -1) bonusLi = li;
              else nonBonusCount++;
            });
            const liBeingZeroed = document.getElementById(
              `CartDrawer-Item-${line}`,
            );
            const liIsBonus =
              liBeingZeroed &&
              liBeingZeroed.innerHTML.indexOf(bonusMark) !== -1;
            if (!liIsBonus && nonBonusCount === 1 && bonusLi) {
              if (typeof this.enableLoading === "function")
                this.enableLoading(line);
              removeMainAndBonusAtomically(liBeingZeroed, bonusLi).finally(
                () => {
                  if (typeof this.disableLoading === "function")
                    this.disableLoading(line);
                },
              );
              return;
            }
          }

          const qtyInput =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          if (qtyInput) qtyInput.value = quantity;
          if (typeof this.enableLoading === "function") this.enableLoading(line);

          fetch(`${window.routes.cart_change_url}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/javascript",
              "X-Requested-With": "XMLHttpRequest",
            },
            credentials: "same-origin",
            body: JSON.stringify({
              line,
              quantity,
              sections: ["cart-drawer", "cart-icon-bubble"],
              sections_url: window.location.pathname,
            }),
          })
            .then((r) => r.text())
            .then((state) => {
              const parsedState = JSON.parse(state);
              if (parsedState.errors) {
                const qtyEl =
                  document.getElementById(`Quantity-${line}`) ||
                  document.getElementById(`Drawer-quantity-${line}`);
                if (qtyEl) qtyEl.value = qtyEl.getAttribute("value");
                if (typeof this.updateLiveRegions === "function")
                  this.updateLiveRegions(line, parsedState.errors);
                return;
              }

              this.classList.toggle("is-empty", parsedState.item_count === 0);
              const cartDrawerWrapper = document.querySelector("cart-drawer");
              if (cartDrawerWrapper) {
                cartDrawerWrapper.classList.toggle(
                  "is-empty",
                  parsedState.item_count === 0,
                );
              }

              // qty=0 → item removed, line indexes shift. Use full items
              // list replace. For qty>0 → ultra-surgical price-only update.
              applySurgicalDrawerUpdate(parsedState.sections, {
                changedLine: quantity === 0 ? null : line,
              });

              // source: "cart-items" → CartDrawerItems' inherited subscriber
              // self-filters this and skips the broken main-cart-items refetch
              if (
                typeof publish === "function" &&
                typeof PUB_SUB_EVENTS !== "undefined"
              ) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
              }
            })
            .catch((e) => console.error("[updateQuantity]", e))
            .finally(() => {
              if (typeof this.disableLoading === "function")
                this.disableLoading(line);
            });
        };
      })
      .catch(() => {});
  }

  // Intercept remove clicks: if removing the LAST non-bonus item while bonus
  // is in the cart, zero out BOTH lines in a single /cart/update.js so the
  // bonus disappears at the exact same moment as the last product.
  //
  // CRITICAL: the SYNC decision + stopPropagation must happen BEFORE any
  // await, otherwise the event continues propagating and the default
  // cart-remove-button handler fires first.
  function handleRemoveClick(e) {
    const removeBtn = e.target.closest("cart-remove-button");
    if (!removeBtn) return;

    const drawerItems = removeBtn.closest("cart-drawer-items");
    if (!drawerItems) return;

    // Item the user is removing
    const liBeingRemoved = removeBtn.closest("li");
    if (!liBeingRemoved) return;

    // Sync check: is the clicked item the bonus itself? If so, allow default.
    const bonusMark = String(BONUS_VARIANT_ID);
    if (liBeingRemoved.innerHTML.indexOf(bonusMark) !== -1) return;

    // Sync count non-bonus items + locate bonus item element
    const allItems = drawerItems.querySelectorAll(".wt-cart__list > li");
    let nonBonusCount = 0;
    let bonusLi = null;
    allItems.forEach((li) => {
      if (li.innerHTML.indexOf(bonusMark) !== -1) {
        bonusLi = li;
      } else {
        nonBonusCount++;
      }
    });

    // Only intercept: removing the LAST non-bonus item AND bonus is present
    if (nonBonusCount !== 1 || !bonusLi) return;

    // SYNC INTERCEPT — must happen before any await
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Async cleanup work
    removeMainAndBonusAtomically(liBeingRemoved, bonusLi);
  }

  async function removeMainAndBonusAtomically(mainLi, bonusLi) {
    window.__instantAddBusy = true;
    const cartDrawer = getCartDrawer();
    const sectionIds = getSectionIds(cartDrawer);

    try {
      // Resolve line item keys via /cart.js (need them for an atomic update)
      const cart = await fetch("/cart.js", {
        credentials: "same-origin",
      }).then((r) => r.json());

      // Map indices to li elements — the order matches cart.items
      const mainIndex = parseInt(
        mainLi.id.replace(/^CartDrawer-Item-/, ""),
        10,
      );
      const bonusIndex = parseInt(
        bonusLi.id.replace(/^CartDrawer-Item-/, ""),
        10,
      );

      const mainItem = (cart.items || [])[mainIndex - 1];
      const bonusItem = (cart.items || [])[bonusIndex - 1];

      if (!mainItem || !bonusItem) {
        throw new Error("Could not resolve cart line items");
      }

      const updates = {};
      updates[mainItem.key] = 0;
      updates[bonusItem.key] = 0;

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          updates,
          sections: sectionIds.join(","),
          sections_url: window.location.pathname,
        }),
        credentials: "same-origin",
      }).then((r) => r.json());

      try {
        localStorage.removeItem("bonus_pb_gift");
      } catch (err) {}

      // Surgical update — replace only items list + footer. The
      // bonus-progress-bar element stays in DOM, so its width smoothly
      // animates to 0% via CSS transition (instead of disappearing instantly).
      applySurgicalDrawerUpdate(response.sections, { changedLine: null });

      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cart: response,
        });
      }
    } catch (err) {
      console.error("[instant-add] atomic remove failed", err);
    } finally {
      window.__instantAddBusy = false;
      if (typeof window.bonusPbForceUpdate === "function") {
        window.bonusPbForceUpdate();
      }
    }
  }

  function handleInstantAddClick(e) {
    const btn = e.target.closest('[data-instant-add="true"]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    addToCartInstantly(btn, parseInt(variantId, 10));
  }

  function getCartDrawer() {
    return document.querySelector("cart-drawer");
  }

  function getSectionIds(cartDrawer) {
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
      try {
        return cartDrawer.getSectionsToRender().map((s) => s.id);
      } catch (e) {}
    }
    return ["cart-drawer", "cart-icon-bubble"];
  }

  function readVariantPrice(btn) {
    var dp = btn.dataset.price || btn.dataset.variantPrice;
    if (dp) {
      var n = parseInt(dp, 10);
      if (!isNaN(n)) return n;
    }
    // Try price element near the button
    var card = btn.closest(
      "[data-product-handle], .card, .grid__item, .wt-card",
    );
    if (card) {
      var priceEl = card.querySelector(
        "[data-price], .price-item--sale, .price-item--regular, .price__current, .card__button-price",
      );
      if (priceEl) {
        var txt = priceEl.textContent.replace(/[^\d.,]/g, "").replace(",", ".");
        var num = parseFloat(txt);
        if (!isNaN(num)) return Math.round(num * 100);
      }
    }
    return null;
  }

  async function postAdd(items, sectionIds) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        items: items,
        sections: sectionIds.join(","),
        sections_url: window.location.pathname,
      }),
      credentials: "same-origin",
    });
    if (!r.ok) {
      const err = await r.text().catch(() => "");
      throw new Error("cart/add failed: " + r.status + " " + err.slice(0, 200));
    }
    return r.json();
  }

  function cartTotalsExcludingBonus(cart) {
    let total = 0;
    let hasBonus = false;
    for (const it of cart.items || []) {
      if (
        it.variant_id === BONUS_VARIANT_ID &&
        it.properties &&
        it.properties[BONUS_PROP_KEY] === "true"
      ) {
        hasBonus = true;
        continue;
      }
      total += it.final_line_price || 0;
    }
    return { total, hasBonus };
  }

  async function addToCartInstantly(btn, variantId) {
    if (btn.classList.contains("loading")) return;

    const labelEl = btn.querySelector(
      '.card__button-label, .ai-button-label, [class*="button-label"]',
    );
    const originalContent = labelEl ? labelEl.textContent : "";

    btn.classList.add("loading");
    btn.disabled = true;
    if (labelEl) labelEl.textContent = "Додаємо...";

    // Block bonus-progress-bar racing during the flow
    window.__instantAddBusy = true;

    const cartDrawer = getCartDrawer();
    const sectionIds = getSectionIds(cartDrawer);

    try {
      const variantPrice = readVariantPrice(btn);
      let response;
      let bonusAddedNow = false;

      // Serialize the read-cart → decide-bonus → POST block via __addQueue so
      // that concurrent clicks in THIS tab run one-by-one: the 2nd/3rd click
      // sees the bonus the 1st already added and does NOT add another. The
      // bonus is added ATOMICALLY together with the product in one POST, so it
      // appears in the drawer instantly alongside the product.
      const myTurn = __addQueue.then(async () => {
        if (variantPrice != null && variantId !== BONUS_VARIANT_ID) {
          // FAST PATH: pre-check via /cart.js, then ONE multi-item POST
          const cart = await fetch("/cart.js", {
            credentials: "same-origin",
          }).then((r) => r.json());

          const { total, hasBonus } = cartTotalsExcludingBonus(cart);
          const newTotal = total + variantPrice; // qty=1

          const items = [{ id: variantId, quantity: 1 }];
          if (!hasBonus && newTotal >= BONUS_THRESHOLD_KOP) {
            items.push({
              id: BONUS_VARIANT_ID,
              quantity: 1,
              properties: { [BONUS_PROP_KEY]: "true" },
            });
            bonusAddedNow = true;
          }

          response = await postAdd(items, sectionIds);
        } else {
          // FALLBACK PATH (no price available): POST main, then maybe bonus
          response = await postAdd(
            [{ id: variantId, quantity: 1 }],
            sectionIds,
          );

          if (variantId !== BONUS_VARIANT_ID) {
            try {
              const cart = await fetch("/cart.js", {
                credentials: "same-origin",
              }).then((r) => r.json());
              const { total, hasBonus } = cartTotalsExcludingBonus(cart);
              if (!hasBonus && total >= BONUS_THRESHOLD_KOP) {
                response = await postAdd(
                  [
                    {
                      id: BONUS_VARIANT_ID,
                      quantity: 1,
                      properties: { [BONUS_PROP_KEY]: "true" },
                    },
                  ],
                  sectionIds,
                );
                bonusAddedNow = true;
              }
            } catch (e) {}
          }
        }
      });
      __addQueue = myTurn.catch(() => {}); // keep queue alive on errors
      await myTurn;

      if (bonusAddedNow) {
        try {
          localStorage.setItem("bonus_pb_gift", "1");
        } catch (e) {}
      }

      // Publish cartUpdate BEFORE renderContents so that subscribers (notably
      // the stale CartDrawerItems instance about to be replaced) handle the
      // event against the OLD detached element — not the freshly-mounted one.
      // This prevents a second-stage re-render of the drawer body.
      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "instant-add",
          cart: response,
        });
      }
      document.dispatchEvent(
        new CustomEvent("cart:item-added", {
          detail: { items: response.items },
        }),
      );

      // Drawer opens HERE — already populated with main + bonus
      if (
        cartDrawer &&
        response.sections &&
        typeof cartDrawer.renderContents === "function"
      ) {
        const isClosedCart = !document.body.classList.contains(
          "page-overlay-cart-on",
        );
        cartDrawer.renderContents(response, isClosedCart);
      } else if (
        cartDrawer &&
        typeof cartDrawer.toggleDrawerClasses === "function"
      ) {
        document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
        if (!cartDrawer.isOpen) {
          setTimeout(() => cartDrawer.toggleDrawerClasses(), 100);
        }
      }

      if (labelEl) labelEl.textContent = "Додано ✓";
      btn.classList.remove("loading");
      btn.classList.add("added");
      setTimeout(() => {
        if (labelEl) labelEl.textContent = originalContent;
        btn.classList.remove("added");
        btn.disabled = false;
      }, 1500);
    } catch (error) {
      console.error("[instant-add] error:", error);
      if (labelEl) labelEl.textContent = "Помилка";
      setTimeout(() => {
        if (labelEl) labelEl.textContent = originalContent;
        btn.classList.remove("loading");
        btn.disabled = false;
      }, 2000);
    } finally {
      window.__instantAddBusy = false;
      if (typeof window.bonusPbForceUpdate === "function") {
        window.bonusPbForceUpdate();
      }
      // Hard clamp bonus to <=1 right after every add — belt from the
      // instant-add side, independent of the progress bar's interceptor timing.
      if (typeof window.bonusClampToOne === "function") {
        window.bonusClampToOne();
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initInstantAdd);
  } else {
    initInstantAdd();
  }
  document.addEventListener("shopify:section:load", () => {
    initialized = false;
    initInstantAdd();
  });
})();
